import React, { useEffect, useMemo, useState } from "react";
import '../../../node_modules/bootstrap/dist/css/bootstrap.min.css';
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import SearchSessionResult from "./partials/SearchSessionResult";
import { generateFinalDecisionPDF } from "./partials/FinalDecisionPDFReport";

import {
    getFinalDecisions as onGetFinalDecisions,
} from "store/actions";

import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

import { Col, Row, Card, CardBody, Button, Badge, Container } from "reactstrap";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";
import { useParams } from "react-router-dom";

const FinalDecision = () => {
    document.title = "Décisions Finales de Délibération | CinzanEdu";

    const dispatch = useDispatch();
    const { sessionUuid } = useParams();

    const [filters, setFilters] = useState({});
    const [studyLevelName, setStudyLevelName] = useState("");

    // --- Selector adapté pour les décisions finales ---
    const FinalDecisionProperties = createSelector(
        (state) => state.finalDecisionReducer,
        (finalDecisionReducer) => ({
            finalDecisions: finalDecisionReducer.finalDecisions || [], 
            sessionInfo:    finalDecisionReducer.sessionInfo    || null,
            loading:        finalDecisionReducer.loading,
        })
    );

    const {
        finalDecisions,
        sessionInfo,
        loading,
    } = useSelector(FinalDecisionProperties);

    const [isLoading, setLoading] = useState(true);

    useEffect(() => {
        if (sessionUuid) {
            dispatch(onGetFinalDecisions({ 
                session_uuid: sessionUuid,
                ...filters
            }));
        }
    }, [dispatch, sessionUuid]);

    const handleSearch = (newFilters) => {
        setStudyLevelName(newFilters.study_level_label);
        const payload = {
            session_uuid: sessionUuid,
            study_level_id: newFilters.study_level_id,
        };
        setFilters(payload);
        dispatch(onGetFinalDecisions(payload));
    };

    // --- Configuration des colonnes avec Traduction des Statuts ---
    const columns = useMemo(() => [
        {
            header: "Matricule",
            accessorKey: "student_number",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Nom",
            accessorKey: "last_name",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Prénom",
            accessorKey: "first_name",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Sexe",
            accessorKey: "sex",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Date Naiss.",
            accessorKey: "birth_date",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cell) => cell.getValue() ? cell.getValue() : "-",
        },
        
        {
            header: "Décision",
            accessorKey: "final_status",
            enableColumnFilter: false,
            enableSorting: true,
            cell: (cell) => {
                const status = cell.getValue();
                
                // --- TRADUCTION ET COULEURS ---
                const statusConfig = {
                    'ADMITTED': { label: 'ADMISSIBLE', color: 'success' },
                    'ADMITTED_WITH_DEBT': { label: 'ADMISSIBLE (AVEC CREDIT)', color: 'info' },
                    'RETAKE': { label: 'AJOURNÉ(E)', color: 'warning' },
                    'REPEATING': { label: 'REDOUBLANT(E)', color: 'danger' }
                };

                const config = statusConfig[status] || { label: status, color: 'secondary' };

                return (
                    <Badge color={config.color} className="font-size-11 text-uppercase p-2">
                        {config.label}
                    </Badge>
                );
            }
        }
    ], []);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs
                        title="Délibération"
                        breadcrumbItem={
                            sessionInfo
                                ? `Résultats Annuels — ${sessionInfo.label}`
                                : "Décisions de fin d'année"
                        }
                    />

                    <SearchSessionResult onSearch={handleSearch} />

                    {loading ? <Spinners setLoading={setLoading} /> :
                        <Row>
                            <Col lg="12">
                                <Card>
                                    <CardBody className="border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <div>
                                            <h5 className="mb-0">Procès-verbal de délibération annuelle</h5>
                                            <p className="text-muted mb-0 small">
                                                Niveau : {studyLevelName || "Tous les niveaux"}
                                            </p>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <Badge color="soft-primary" className="p-2 font-size-13">
                                                {finalDecisions.length} étudiant(s) délibéré(s)
                                            </Badge>
                                            
                                            <Button
                                                color="primary"
                                                outline
                                                onClick={() => generateFinalDecisionPDF({ 
                                                    results: finalDecisions, 
                                                    sessionInfo: sessionInfo,
                                                    studyLevelName: studyLevelName 
                                                })}
                                            >
                                                <i className="bx bx-printer me-1"></i> Imprimer PDF
                                            </Button>
                                        </div>
                                    </CardBody>

                                    <CardBody>
                                        <TableContainer
                                            columns={columns}
                                            data={finalDecisions || []}
                                            isCustomPageSize={true}
                                            isGlobalFilter={true}
                                            isPagination={true}
                                            SearchPlaceholder="Rechercher par nom ou matricule..."
                                            tableClass="align-middle table-nowrap table-hover"
                                            theadClass="table-light"
                                            pagination="pagination"
                                            paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    }
                </Container>
            </div>
            <ToastContainer />
        </React.Fragment>
    );
};

export default FinalDecision;